// src/pages/platform/PlatformFederationPeerPage.tsx
// Federation peer detail — connection info, sync config, data residency, health, shared resources.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, usePlatformFederation, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import {
  mapFederationStatusToVariant,
  mapTrustLevelToVariant,
  formatStatus,
} from '../../features/platform/mapStatusToVariant';
import { Select } from '@/components/Select';

// ---------------------------------------------------------------------------
// Toggle
// ---------------------------------------------------------------------------

function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-center justify-between py-3 ${disabled ? 'opacity-50' : ''}`}>
      <div>
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-muted'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Coerce feature-level variants (which may include 'info') to Badge-supported variants. */
function asBadgeVariant(
  v: string
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' {
  return v === 'info'
    ? 'default'
    : (v as 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error');
}

function formatDate(d?: string): string {
  if (!d) return '--';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlatformFederationPeerPage = observer(() => {
  const { id, peerId } = useParams<{ id: string; peerId: string }>();
  const store = usePlatform();
  const fedStore = usePlatformFederation();
  const { t } = useI18n();

  const [peer, setPeer] = useState<any>(null);
  const [healthHistory, setHealthHistory] = useState<any[]>([]);
  const [syncConfig, setSyncConfig] = useState({
    sync_product_catalog: false,
    sync_plan_catalog: false,
    allow_cross_subscription: false,
    allow_identity_federation: false,
    allow_resource_linking: false,
    sync_interval_minutes: 60,
    heartbeat_interval_seconds: 30,
    cache_ttl_minutes: 15,
  });
  const [dataPolicy, setDataPolicy] = useState({
    region: '',
    allowed_regions: [] as string[],
    blocked_regions: [] as string[],
    level: '',
    compliance_framework: '',
    data_processor_agreement_id: '',
    allow_metadata_export: false,
    allow_aggregated_analytics: false,
    allow_user_directory: false,
    allow_resource_preview: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (id) store.GetPlatform(id);
    if (peerId) {
      loadPeer();
    }
  }, [id, peerId]);

  const loadPeer = async () => {
    if (!peerId) return;
    try {
      const res = await fedStore.GetFederationPeer(peerId);
      const p = res?.peer;
      setPeer(p ?? null);
      if (p?.sync_config) {
        setSyncConfig({
          sync_product_catalog: p.sync_config.sync_product_catalog ?? false,
          sync_plan_catalog: p.sync_config.sync_plan_catalog ?? false,
          allow_cross_subscription: p.sync_config.allow_cross_subscription ?? false,
          allow_identity_federation: p.sync_config.allow_identity_federation ?? false,
          allow_resource_linking: p.sync_config.allow_resource_linking ?? false,
          sync_interval_minutes: p.sync_config.sync_interval_minutes ?? 60,
          heartbeat_interval_seconds: p.sync_config.heartbeat_interval_seconds ?? 30,
          cache_ttl_minutes: p.sync_config.cache_ttl_minutes ?? 15,
        });
      }
      if (p?.data_policy) {
        setDataPolicy({
          region: p.data_policy.region ?? '',
          allowed_regions: p.data_policy.allowed_regions ?? [],
          blocked_regions: p.data_policy.blocked_regions ?? [],
          level: p.data_policy.level ?? '',
          compliance_framework: p.data_policy.compliance_framework ?? '',
          data_processor_agreement_id: p.data_policy.data_processor_agreement_id ?? '',
          allow_metadata_export: p.data_policy.allow_metadata_export ?? false,
          allow_aggregated_analytics: p.data_policy.allow_aggregated_analytics ?? false,
          allow_user_directory: p.data_policy.allow_user_directory ?? false,
          allow_resource_preview: p.data_policy.allow_resource_preview ?? false,
        });
      }

      // Load health history
      try {
        const hRes = await fedStore.GetFederationHealthHistory(peerId);
        const checks = hRes?.items?.items;
        setHealthHistory(Array.isArray(checks) ? checks : []);
      } catch {
        // Health history may not be available
      }
    } catch {
      // Handled by store
    }
  };

  const handleSave = async () => {
    if (!peerId) return;
    setIsSaving(true);
    try {
      await fedStore.UpdateFederationPeer(peerId, {
        sync_config: syncConfig,
        data_policy: dataPolicy,
      } as any);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      loadPeer();
    } catch {
      // Handled by store
    } finally {
      setIsSaving(false);
    }
  };

  const handleAccept = async () => {
    if (!peerId) return;
    try {
      await fedStore.AcceptFederationPeer(peerId, {} as any);
      loadPeer();
    } catch {
      // Handled
    }
  };

  const handleRevoke = async () => {
    if (!peerId || !confirm(t('platform.federation.revokeConfirm'))) return;
    try {
      await fedStore.RevokeFederationPeer(peerId, {} as any);
      loadPeer();
    } catch {
      // Handled
    }
  };

  const handleSync = async () => {
    if (!peerId) return;
    try {
      await fedStore.SyncFederationCatalog(peerId, {} as any);
      loadPeer();
    } catch {
      // Handled
    }
  };

  const handleCheckHealth = async () => {
    if (!peerId) return;
    try {
      await fedStore.CheckFederationHealth(peerId, {} as any);
      loadPeer();
    } catch {
      // Handled
    }
  };

  const platformName = (store.currentItem as any)?.name ?? '';
  const status = peer?.status ?? '';
  const isPending = status.includes('PENDING');
  const isRevoked = status.includes('REVOKED');

  if (fedStore.isLoading && !peer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (fedStore.error && !peer) {
    return (
      <>
        <ErrorAlert error={fedStore.error} />
        <Link to={`/platforms/${id}/federation`} className="mt-2 inline-block text-sm underline text-primary">
          {t('platform.federation.backToFederation')}
        </Link>
      </>
    );
  }

  if (!peer) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to={`/platforms/${id}/federation`} className="mt-2 inline-block text-sm underline text-primary">
          {t('platform.federation.backToFederation')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
              {t('platform.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to={`/platforms/${id}`} className="text-muted-foreground hover:text-foreground">
              {platformName}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to={`/platforms/${id}/federation`} className="text-muted-foreground hover:text-foreground">
              {t('platform.federation.title')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{peer.name}</span>
          </nav>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{peer.name}</h1>
            <Badge variant={asBadgeVariant(mapFederationStatusToVariant(peer.status))}>
              {formatStatus(peer.status)}
            </Badge>
            <Badge variant={asBadgeVariant(mapTrustLevelToVariant(peer.trust_level))}>
              {formatStatus(peer.trust_level)}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          {isPending && (
            <Button onClick={handleAccept}>
              {t('platform.federation.accept')}
            </Button>
          )}
          {!isRevoked && (
            <>
              <Button variant="secondary" onClick={handleSync}>
                {t('platform.federation.sync')}
              </Button>
              <Button variant="secondary" onClick={handleCheckHealth}>
                {t('platform.federation.checkHealth')}
              </Button>
              <Button variant="danger" onClick={handleRevoke}>
                {t('platform.federation.revoke')}
              </Button>
            </>
          )}
        </div>
      </div>

      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('platform.federation.saveSuccess')}
        </Alert>
      )}

      {/* Connection Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.federation.connectionInfo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Peer Platform ID</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{peer.peer_platform_id ?? '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Endpoint</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{peer.peer_endpoint ?? '--'}</dd>
            </div>
            {peer.peer_public_key && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Public Key</dt>
                <dd className="text-sm text-foreground col-span-2">
                  <code className="text-xs bg-muted px-2 py-1 rounded break-all">{peer.peer_public_key}</code>
                </dd>
              </div>
            )}
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created By</dt>
              <dd className="text-sm text-foreground col-span-2">
                {peer.created_by?.display_name ?? peer.created_by?.entity?.id ?? '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(peer.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Accepted At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(peer.accepted_at)}</dd>
            </div>
            {peer.revoked_at && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Revoked At</dt>
                <dd className="text-sm text-foreground col-span-2">{formatDate(peer.revoked_at)}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Trust & Sync Config */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.federation.trustSyncConfig')}</CardTitle>
          <CardDescription>
            {t('platform.federation.trustSyncDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <Toggle
            label="Sync Product Catalog"
            description="Sync product metadata (names, descriptions, plans)"
            checked={syncConfig.sync_product_catalog}
            onChange={(v) => setSyncConfig((s) => ({ ...s, sync_product_catalog: v }))}
            disabled={isRevoked}
          />
          <Toggle
            label="Sync Plan Catalog"
            description="Sync subscription plan details"
            checked={syncConfig.sync_plan_catalog}
            onChange={(v) => setSyncConfig((s) => ({ ...s, sync_plan_catalog: v }))}
            disabled={isRevoked}
          />
          <Toggle
            label="Allow Cross-Subscription"
            description="Tenants can subscribe to remote products"
            checked={syncConfig.allow_cross_subscription}
            onChange={(v) => setSyncConfig((s) => ({ ...s, allow_cross_subscription: v }))}
            disabled={isRevoked}
          />
          <Toggle
            label="Allow Identity Federation"
            description="Users can authenticate cross-platform (SSO)"
            checked={syncConfig.allow_identity_federation}
            onChange={(v) => setSyncConfig((s) => ({ ...s, allow_identity_federation: v }))}
            disabled={isRevoked}
          />
          <Toggle
            label="Allow Resource Linking"
            description="Cross-platform resource references via URN"
            checked={syncConfig.allow_resource_linking}
            onChange={(v) => setSyncConfig((s) => ({ ...s, allow_resource_linking: v }))}
            disabled={isRevoked}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Sync Interval (min)</label>
              <Input
                type="number"
                value={String(syncConfig.sync_interval_minutes)}
                onChange={(e) => setSyncConfig((s) => ({ ...s, sync_interval_minutes: Number(e.target.value) }))}
                disabled={isRevoked}
                min={1}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Heartbeat Interval (sec)</label>
              <Input
                type="number"
                value={String(syncConfig.heartbeat_interval_seconds)}
                onChange={(e) => setSyncConfig((s) => ({ ...s, heartbeat_interval_seconds: Number(e.target.value) }))}
                disabled={isRevoked}
                min={1}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Cache TTL (min)</label>
              <Input
                type="number"
                value={String(syncConfig.cache_ttl_minutes)}
                onChange={(e) => setSyncConfig((s) => ({ ...s, cache_ttl_minutes: Number(e.target.value) }))}
                disabled={isRevoked}
                min={0}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Residency Policy */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.federation.dataPolicy')}</CardTitle>
          <CardDescription>
            {t('platform.federation.dataPolicyDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Region</label>
              <Input
                value={dataPolicy.region}
                onChange={(e) => setDataPolicy((d) => ({ ...d, region: e.target.value }))}
                disabled={isRevoked}
                placeholder="eu-west-1"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Compliance Framework</label>
              <Input
                value={dataPolicy.compliance_framework}
                onChange={(e) => setDataPolicy((d) => ({ ...d, compliance_framework: e.target.value }))}
                disabled={isRevoked}
                placeholder="GDPR, SOC2, HIPAA"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Data Residency Level</label>
            <Select
              value={dataPolicy.level}
              onChange={(e) => setDataPolicy((d) => ({ ...d, level: e.target.value }))} disabled={isRevoked} className="w-full sm:w-64">
              <option value="">--</option>
              <option value="DATA_RESIDENCY_LEVEL_STRICT">Strict</option>
              <option value="DATA_RESIDENCY_LEVEL_METADATA_ONLY">Metadata Only</option>
              <option value="DATA_RESIDENCY_LEVEL_AGGREGATED">Aggregated</option>
              <option value="DATA_RESIDENCY_LEVEL_OPEN">Open</option>
            </Select>
          </div>

          <div className="divide-y divide-border">
            <Toggle
              label="Allow Metadata Export"
              description="Entity URNs, names, counts"
              checked={dataPolicy.allow_metadata_export}
              onChange={(v) => setDataPolicy((d) => ({ ...d, allow_metadata_export: v }))}
              disabled={isRevoked}
            />
            <Toggle
              label="Allow Aggregated Analytics"
              description="Anonymized statistics"
              checked={dataPolicy.allow_aggregated_analytics}
              onChange={(v) => setDataPolicy((d) => ({ ...d, allow_aggregated_analytics: v }))}
              disabled={isRevoked}
            />
            <Toggle
              label="Allow User Directory"
              description="Public profile data (name, avatar)"
              checked={dataPolicy.allow_user_directory}
              onChange={(v) => setDataPolicy((d) => ({ ...d, allow_user_directory: v }))}
              disabled={isRevoked}
            />
            <Toggle
              label="Allow Resource Preview"
              description="Resource titles and previews (not full content)"
              checked={dataPolicy.allow_resource_preview}
              onChange={(v) => setDataPolicy((d) => ({ ...d, allow_resource_preview: v }))}
              disabled={isRevoked}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Allowed Regions</label>
              <Input
                value={(dataPolicy.allowed_regions ?? []).join(', ')}
                onChange={(e) =>
                  setDataPolicy((d) => ({
                    ...d,
                    allowed_regions: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean),
                  }))
                }
                disabled={isRevoked}
                placeholder="eu-west-1, eu-central-1"
              />
              <p className="text-xs text-muted-foreground">Comma-separated region codes</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Blocked Regions</label>
              <Input
                value={(dataPolicy.blocked_regions ?? []).join(', ')}
                onChange={(e) =>
                  setDataPolicy((d) => ({
                    ...d,
                    blocked_regions: e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean),
                  }))
                }
                disabled={isRevoked}
                placeholder="us-east-1"
              />
              <p className="text-xs text-muted-foreground">Comma-separated region codes</p>
            </div>
          </div>

          {dataPolicy.data_processor_agreement_id && (
            <div className="py-2">
              <span className="text-sm font-medium text-muted-foreground">DPA ID: </span>
              <span className="text-sm text-foreground font-mono">{dataPolicy.data_processor_agreement_id}</span>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving || isRevoked}>
            {isSaving ? t('common.saving') : t('common.saveChanges')}
          </Button>
        </CardFooter>
      </Card>

      {/* Health */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.federation.health')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Last Heartbeat</dt>
              <dd className="text-sm text-foreground col-span-2">
                {peer.last_heartbeat ? (
                  <span>
                    {formatDate(peer.last_heartbeat)}{' '}
                    <span className="text-muted-foreground">({relativeTime(peer.last_heartbeat)})</span>
                  </span>
                ) : (
                  '--'
                )}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Last Sync</dt>
              <dd className="text-sm text-foreground col-span-2">
                {peer.last_sync_at ? (
                  <span>
                    {formatDate(peer.last_sync_at)}{' '}
                    <span className="text-muted-foreground">({relativeTime(peer.last_sync_at)})</span>
                  </span>
                ) : (
                  '--'
                )}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Sync Failures</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(peer.sync_failure_count ?? 0) > 0 ? 'error' : 'success'}>
                  {peer.sync_failure_count ?? 0}
                </Badge>
              </dd>
            </div>
            {peer.last_error && (
              <div className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">Last Error</dt>
                <dd className="text-sm text-destructive col-span-2 font-mono text-xs bg-muted p-2 rounded">
                  {peer.last_error}
                </dd>
              </div>
            )}
          </dl>

          {/* Health check history */}
          {healthHistory.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-foreground mb-3">Health Check History</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {healthHistory.map((entry: any, idx: number) => (
                  <div
                    key={entry.entity?.id ?? idx}
                    className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          entry.healthy || entry.status === 'HEALTHY' ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      />
                      <span className="text-foreground">
                        {entry.healthy || entry.status === 'HEALTHY' ? 'Healthy' : 'Unhealthy'}
                      </span>
                      {entry.latency_ms != null && (
                        <span className="text-muted-foreground">({entry.latency_ms}ms)</span>
                      )}
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {formatDate(entry.checked_at ?? entry.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Shared Resources */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.federation.sharedResources')}</CardTitle>
          <CardDescription>
            {t('platform.federation.sharedResourcesDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Shared Product Slugs</h4>
            {(peer.shared_product_slugs ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {peer.shared_product_slugs.map((slug: string) => (
                  <Badge key={slug} variant="secondary">{slug}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('platform.federation.noSharedProducts')}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Shared Scopes</h4>
            {(peer.shared_scopes ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {peer.shared_scopes.map((scope: string) => (
                  <Badge key={scope} variant="default">{scope}</Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('platform.federation.noSharedScopes')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default PlatformFederationPeerPage;
